// F6 / Shift+F6 moves focus between the panes on screen (PARITY P339; Windows KeyboardMap.NextPane):
// sidebar → conversation → side panel (thread / channel info), wrapping (core/keyboardPanes.ts).
// Focus lands on the pane's natural target: the sidebar's selected row (else its search field), the
// conversation's composer (else its timeline), the panel's composer (else its first control).
//
// Ctrl+Tab / Ctrl+Shift+Tab steps conversations on Windows. Every browser RESERVES it (and
// Ctrl+PgUp/PgDn) for its own tabs — a page never receives it — so it is answered only where it IS
// delivered (some installed-app windows) and Alt+↑/↓ (Workspace) stays the browser's way to step.
import { useEffect } from 'react';
import { KeyboardPanes } from '../../../core/keyboardPanes';

// The conversation sits inside <main class=workspace-main> (display: contents, P255).
const PANES = ['.workspace > .sidebar', '.workspace > .workspace-main > .conversation-pane:not(.side-panel):not(.thread-pane)', '.workspace > .side-panel, .workspace > .thread-pane'];

function paneElements(): Array<HTMLElement | null> {
  return PANES.map((sel) => {
    const el = document.querySelector<HTMLElement>(sel);
    return el && el.offsetParent !== null ? el : null;
  });
}

function focusTarget(pane: HTMLElement, index: number): HTMLElement | null {
  const pick = (sel: string) => pane.querySelector<HTMLElement>(sel);
  if (index === 0) return pick('[aria-current="true"]') ?? pick('input[type="search"]');
  return pick('textarea:not([disabled])') ?? pick('.timeline') ?? pick('button, [tabindex="0"]');
}

function stepPanes(backwards: boolean): boolean {
  const panes = paneElements();
  const active = document.activeElement;
  const current = panes.findIndex((p) => !!p && !!active && p.contains(active));
  const next = KeyboardPanes.nextPane(panes.map((p) => !!p), current, backwards);
  if (next < 0) return false;
  const pane = panes[next]!;
  const target = focusTarget(pane, next);
  if (target) {
    if (!target.hasAttribute('tabindex') && !/^(INPUT|TEXTAREA|BUTTON|A)$/.test(target.tagName)) target.tabIndex = -1;
    target.focus();
  }
  return true;
}

/** Installs F6 / Shift+F6 and the Ctrl+Tab step (where the browser delivers it). */
export function usePaneKeys(stepConversation: (direction: 'previous' | 'next') => void): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Inert under anything modal (KeyboardMap.Allows).
      if (document.querySelector('.scrim, .dialog, .lightbox')) return;
      if (e.key === 'F6' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (stepPanes(e.shiftKey)) e.preventDefault();
      } else if (e.key === 'Tab' && e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        stepConversation(e.shiftKey ? 'previous' : 'next');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stepConversation]);
}
